/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { When } from "react-if";
import styled from "styled-components";
import { FlexContainer, SVGIcon, Text, theme } from "@nordcloud/gnui";
import { dateFormat } from "~/constants";
import { useBillingPeriod } from "~/services/customers";
import { capitalize, isNil } from "~/tools";
import { BillingPeriodIcon } from "./HeaderIcons";
import {
  ArrowWrap,
  BoxWrap,
  DropdownWrapper,
  DropdownWrapStyle,
} from "./styles";

export function Timeframe() {
  const {
    billingPeriod,
    isCurrentBillingPeriod,
    activatePreviousBillingPeriod,
    activateCurrentBillingPeriod,
  } = useBillingPeriod();

  return (
    <DropdownWrapStyle>
      <BoxWrap>
        <TimeframeWrapper>
          <FlexContainer
            className="billing-period-summary"
            direction="column"
            justifyContent="flex-end"
            alignItems="flex-start"
          >
            <Text mb={0} color={theme.color.text.text02} size="sm">
              {capitalize(billingPeriod)} Month
            </Text>
            <Text mb={0} size="sm">
              {isCurrentBillingPeriod
                ? dayjs().format(dateFormat.monthYear)
                : dayjs().subtract(1, "month").format(dateFormat.monthYear)}
            </Text>
          </FlexContainer>
          <section className="billing-period-icon-wrapper">
            <BillingPeriodIcon />
          </section>
          <ArrowWrap>
            <SVGIcon name="down" />
          </ArrowWrap>
        </TimeframeWrapper>
        <DropdownWrapper>
          <DropdownItem
            isSelected
            period={isCurrentBillingPeriod ? "current" : "previous"}
          />
          <DropdownItem
            period={isCurrentBillingPeriod ? "previous" : "current"}
            onClick={
              isCurrentBillingPeriod
                ? activatePreviousBillingPeriod
                : activateCurrentBillingPeriod
            }
          />
        </DropdownWrapper>
      </BoxWrap>
    </DropdownWrapStyle>
  );
}

const TimeframeWrapper = styled(FlexContainer)`
  border-radius: ${theme.radiusDefault};
  column-gap: ${theme.spacing.spacing02};
  padding: calc(0.375rem + 1px) ${theme.spacing.spacing02};

  :hover {
    background-color: ${theme.color.interactive.secondaryHover};
  }

  .billing-period-icon-wrapper {
    display: none;
  }

  @media screen and (max-width: ${theme.breakpoints.xl}px) {
    padding: 0.375rem ${theme.spacing.spacing02};

    .billing-period-summary {
      display: none;
    }

    .billing-period-icon-wrapper {
      display: block;
      height: 38px;
    }
  }
`;

type DropdownItemProps = {
  onClick?: () => void;
  period: "current" | "previous";
  isSelected?: boolean;
};

function DropdownItem({
  onClick,
  period,
  isSelected = false,
}: DropdownItemProps) {
  const date = (
    period === "current" ? dayjs() : dayjs().subtract(1, "month")
  ).format(dateFormat.monthYear);

  return (
    <DropdownItemWrapper
      isSelected={isSelected}
      isClickable={isNil(onClick)}
      onClick={() => onClick?.()}
    >
      <FlexContainer columnGap={theme.spacing.spacing02}>
        <FlexContainer
          direction="column"
          alignItems="start"
          columnGap={theme.spacing.spacing02}
        >
          <Text mb={0} color={theme.color.text.text02} size="sm" as="span">
            {capitalize(period)} Month
          </Text>
          <Text mb={0} size="sm">
            {date}
          </Text>
        </FlexContainer>
        <When condition={isSelected}>
          <SVGIcon name="success" color="success" />
        </When>
      </FlexContainer>
    </DropdownItemWrapper>
  );
}

const DropdownItemWrapper = styled.button<{
  isSelected: boolean;
  isClickable: boolean;
}>`
  display: ${({ isSelected }) => (isSelected ? "none" : "flex")};
  background-color: transparent;
  border: none;
  outline: none;
  width: 100%;
  padding: 0;

  > * {
    border-radius: ${theme.radiusDefault};
    justify-content: space-between;
    margin: ${theme.spacing.spacing01};
    padding: calc(${theme.spacing.spacing02} - 1px) ${theme.spacing.spacing02};
    background-color: ${({ isClickable }) =>
      isClickable ? theme.color.interactive.secondaryActive : undefined};
    cursor: ${({ isClickable }) => (isClickable ? "default" : "cursor")};
    width: 100%;

    &:hover {
      background-color: ${({ isClickable }) =>
        isClickable ? undefined : theme.color.interactive.secondaryHover};
    }
  }

  @media screen and (max-width: ${theme.breakpoints.xl}px) {
    display: flex;
  }
`;
